import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2, RotateCcw, ShieldAlert, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { streamMessage, TEAL, TROUBLE } from "@/lib/copilot";
import Rich from "./Rich";
import ActionCard from "./ActionCard";

const LIMIT = "You've reached today's Copilot limit. It resets at midnight.";

function Bubble({ m, hubId, readOnly }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
      <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-[15px] ${mine ? "text-q-text" : "border border-q-border bg-q-surface text-q-muted"}`}
        style={mine ? { background: `rgba(${TEAL},0.16)` } : undefined}>
        {mine ? <p className="whitespace-pre-wrap break-words">{m.content}</p> : <Rich text={m.content} />}
        {m.streaming && !m.content && <span className="inline-flex items-center gap-2 text-sm text-q-faint" data-testid="chat-thinking"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</span>}
        {!!m.sources?.length && <p className="mt-2 text-[11px] text-q-faint" data-testid="chat-sources">Sources: {m.sources.join(", ")}</p>}
        {(m.cards || []).map((c) => <ActionCard key={c.id} hubId={hubId} card={c} readOnly={readOnly} />)}
      </div>
    </div>
  );
}

export default function ChatView({ hubId, cid, onEnsure, suggestions = [], isKid, readOnly, compact }) {
  const [planLimited, setPlanLimited] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const end = useRef(null);
  const skipLoad = useRef(null);

  useEffect(() => {
    setError(null);
    if (!cid) { setMsgs([]); return; }
    if (skipLoad.current === cid) return;
    api.get(`/organizations/${hubId}/copilot/${readOnly ? "kids-chats" : "conversations"}/${cid}${readOnly ? "" : "/messages"}`)
      .then(({ data }) => setMsgs(data.messages)).catch(() => setMsgs([]));
  }, [hubId, cid, readOnly]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs]);

  const run = useCallback(async (id, body) => {
    setBusy(true);
    setError(null);
    setMsgs((p) => [...p, { id: "streaming", role: "assistant", content: "", cards: [], streaming: true }]);
    const patch = (fn) => setMsgs((p) => p.map((m) => (m.id === "streaming" ? fn(m) : m)));
    await streamMessage(hubId, id, body, (ev) => {
      if (ev.type === "delta") patch((m) => ({ ...m, content: m.content + ev.text }));
      if (ev.type === "card") patch((m) => ({ ...m, cards: [...m.cards, ev.card] }));
      if (ev.type === "done") patch((m) => ({ ...m, id: ev.message_id, streaming: false }));
      if (ev.type === "error") {
        setMsgs((p) => p.filter((m) => m.id !== "streaming"));
        const fu = ev.detail?.code === "fair_use_limit";
        const day = fu && new Date(`${ev.detail.resets_on}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric" });
        setError(fu ? (isKid ? `The Copilot is resting until ${day}. Ask a grown-up.` : ev.detail.message) : ev.status === 429 ? LIMIT : ev.message || TROUBLE);
        setPlanLimited(fu && !isKid);
      }
    });
    setBusy(false);
  }, [hubId, isKid]);

  const send = async (t) => {
    const v = (t ?? text).trim();
    if (!v || busy) return;
    setText("");
    const id = cid || (await onEnsure());
    skipLoad.current = id;
    setMsgs((p) => [...p, { id: `u-${Date.now()}`, role: "user", content: v }]);
    run(id, { text: v });
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="chat-view">
      {isKid && <p className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold" style={{ background: `rgba(${TEAL},0.12)`, color: `rgb(${TEAL})` }} data-testid="kid-chat-banner"><ShieldAlert className="h-4 w-4 flex-shrink-0" /> Grown-ups in your family can see these chats.</p>}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-3" data-testid="chat-messages">
        {msgs.length === 0 && !readOnly && (
          <div className={`flex flex-col items-start gap-3 ${compact ? "pt-2" : "pt-8"}`} data-testid="chat-empty">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `rgba(${TEAL},0.15)`, boxShadow: `0 0 30px rgba(${TEAL},0.25)` }}><Sparkles className="h-6 w-6" style={{ color: `rgb(${TEAL})` }} /></span>
            <p className="font-display text-xl font-extrabold text-q-text">{isKid ? "Hi! What do you want to know?" : "What can I help with?"}</p>
            <div className="grid w-full gap-2 sm:grid-cols-2">
              {suggestions.map((s, i) => (
                <button key={s} className="min-h-[44px] rounded-xl border border-q-border bg-q-surface px-3 py-2 text-left text-sm font-semibold text-q-text transition-colors hover:border-[rgba(var(--st-teal),0.6)]"
                  onClick={() => send(s)} data-testid={`chat-suggestion-${i}`}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m) => <Bubble key={m.id} m={m} hubId={hubId} readOnly={readOnly} />)}
        {error && planLimited && <a href={`/h/${hubId}/family/billing`} className="mb-2 inline-block text-sm font-semibold underline" data-testid="copilot-see-plans">See plans</a>}
        {error && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm" data-testid="chat-error">
            <span className="font-semibold text-red-200" data-testid="chat-error-text">{error}</span>
            {error !== LIMIT && <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => run(cid || skipLoad.current, { retry: true })} data-testid="chat-retry-btn"><RotateCcw className="h-4 w-4" /> Retry</button>}
          </div>
        )}
        <div ref={end} />
      </div>
      {!readOnly && (
        <form className="relative mt-2" onSubmit={(e) => { e.preventDefault(); send(); }} data-testid="chat-form">
          <textarea rows={1} className="q-input max-h-40 min-h-[52px] resize-none pr-14" placeholder={isKid ? "Ask me anything…" : "Ask Copilot about your family hub…"} value={text}
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="chat-input" />
          <button type="submit" disabled={busy || !text.trim()} className="absolute bottom-1.5 right-1.5 inline-flex h-11 w-11 items-center justify-center rounded-xl disabled:opacity-40"
            style={{ background: `rgb(${TEAL})`, color: "#042f2a" }} aria-label="Send" data-testid="chat-send-btn">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowUp className="h-5 w-5" />}</button>
        </form>
      )}
    </div>
  );
}
